import type { CacheProxy, NoteActivity } from '@paintscout/api'
import type ApolloCache from '@wora/apollo-cache'
import { useMemo } from 'react'
import { updateCachedNote, useCreateNoteMutation, useDeleteNoteMutation, useUpdateNoteMutation } from '@paintscout/api'
import { useApolloClient } from '@apollo/react-hooks'

const REFRESH_DELAY_MS = 2000
const DEFAULT_REFETCH_QUERIES = ['searchActivities']

/**
 * Returns common useMutations for saving notes and handles updating the caches
 */
export function useNoteMutations() {
  const [deleteNoteMutation] = useDeleteNoteMutation()
  const [updateNoteMutation] = useUpdateNoteMutation()
  const [createNoteMutation] = useCreateNoteMutation()
  const client = useApolloClient()

  return useMemo(() => {
    const scheduleDelayedRefetch = (queries: string[]) => {
      if (!queries?.length) {
        return
      }

      setTimeout(() => {
        const queryManager = (client as any).queryManager
        if (!queryManager?.queries) {
          return
        }
        queryManager.queries.forEach(({ observableQuery }: any) => {
          if (!observableQuery) {
            return
          }

          const queryName = observableQuery.queryName
          if (queries.includes(queryName)) {
            observableQuery.refetch().catch(() => {
              // swallow errors from async refetch
            })
          }
        })
      }, REFRESH_DELAY_MS)
    }

    return {
      async deleteNote(id: string, deleteDoc?: boolean) {
        const result = await deleteNoteMutation({
          variables: {
            id,
            deleteDoc: deleteDoc
          },
          update: (cache: CacheProxy, { data: _data }) => {
            console.log({ cache, _data })
            updateCachedNote(cache, {
              _id: id,
              _deleted: true
            } as NoteActivity)
          }
        })

        scheduleDelayedRefetch(DEFAULT_REFETCH_QUERIES)

        return result
      },

      async createNote({
        docId,
        docType,
        note,
        files,
        skipRefetch = false
      }: {
        docId: string
        docType: string
        note: string
        files?: string[]
        skipRefetch?: boolean
      }) {
        const result = await createNoteMutation({
          variables: {
            docId,
            docType,
            note,
            files
          },
          update(cache: ApolloCache, { data }) {
            if (data?.createNote) {
              updateCachedNote(cache, data.createNote.activity)
            }
          }
        })

        if (!skipRefetch) {
          scheduleDelayedRefetch(DEFAULT_REFETCH_QUERIES)
        }

        return result
      },

      async updateNote({
        id,
        note,
        files,
        skipRefetch = false
      }: {
        id: string
        note: string
        files?: string[]
        skipRefetch?: boolean
      }) {
        const result = await updateNoteMutation({
          variables: {
            id,
            note,
            files
          },
          update(cache: ApolloCache, { data }) {
            if (data?.updateNote) {
              updateCachedNote(cache, data.updateNote.activity)
            }
          }
        })

        if (!skipRefetch) {
          scheduleDelayedRefetch(DEFAULT_REFETCH_QUERIES)
        }

        return result
      }
    }
  }, [client])
}
